<template>
    <component
        :is="as ?? 'button'"
        :class="getClasses"
        :disabled="isDisabled"
        @click.prevent="click"
    >
        <slot />
    </component>
</template>

<style lang="scss">
    @import '../../style/variables';

    #cz2.cz2 {
        .cz2__btn {
            position: relative;

            border-radius: 32px;

            padding: 6px 32px;

            display: inline-flex;
            justify-content: center;
            align-items: center;

            background: #F3F3F7;

            color: color(secondaryBlack);
            font-family: var(--cz-font-700-family);
            font-weight: var(--cz-font-700-weight);
            font-size: 16px;
            text-transform: uppercase;
            text-align: center;
            white-space: nowrap;

            transition: all $transition-fast ease-out;

            &:focus {
                outline: none;
            }

            &:not(:disabled):hover,
            &:not(.cz2__btn--disabled):hover,
            &.cz2__btn--black:focus {
                box-shadow: 0px 4px 2px 1px rgba(0, 0, 0, 0.2);
                transition: all $transition-fast ease-in-out;

                @include respond-above(md) {
                    font-size: 26px;
                }
            }

            // Larger blue drop shadow
            &.on-dark-bg:not(:disabled):hover,
            &.on-dark-bg:not(:disabled):hover {
                box-shadow: 0px 4px 8px 6px rgba(0, 0, 0, 0.2);
            }

            &.cz2__btn--primary {
                background: color(pokemonYellow);
            }

            &.cz2__btn--secondary {
                background: color(primaryGray);
            }

            &.cz2__btn--black {
                background: color(textColor);
            }

            &.cz2__btn--blue {
                background: color(v2BlueDark);
            }

            &.cz2__btn--error {
                background: color(secondaryRed);
            }

            &.cz2__btn--flat {
                background: none;
            }

            &.cz2__btn--black,
            &.cz2__btn--blue,
            &.cz2__btn--error {
                color: color(primaryWhite);
            }

            &.cz2__btn--outline {
                &:after {
                    content: '';

                    position: absolute;
                    left: 0;
                    top: 0;

                    width: 100%;
                    height: 100%;

                    border: 3px solid color(primaryWhite);
                    border-radius: 32px;
                }
            }

            &.cz2__btn--auto {
                padding: 21px 0;
            }

            &.cz2__btn--narrow {
                padding: 21px 60px;
            }

            &.cz2__btn--primary,
            &.cz2__btn--secondary,
            &.cz2__btn--black,
            &.cz2__btn--outline,
            &.cz2__btn--blue {
                &:focus {
                    &:before {
                        content: '';

                        position: absolute;

                        left: -3px;
                        top: -3px;

                        width: calc(100% + 6px);
                        height: calc(100% + 6px);

                        border: 3px solid color(secondaryBlack);
                        border-radius: 32px;
                    }
                }

                &.on-dark-bg {
                    &:focus {
                        &:before {
                            border-color: white;
                        }
                    }
                }
            }

            &.cz2__btn--black:focus {
                &:before {
                    border-color: color(v2YellowLight);
                }
            }

            &:disabled, &.cz2__btn--disabled {
                opacity: .5;
                cursor: not-allowed;
            }
        }
    }

    #cz2.cz2.cz2__is-kiosk {
        .cz2__btn {
            height: 64px;

            font-size: 22px;

            &:not(:disabled):hover, &:not(.cz2__btn--disabled):hover {
                font-size: 26px;
            }
        }
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // Seattle Mariners
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    #cz2.cz2.cz2__is-mariners {
        .cz2__btn--blue,
        .cz2__btn--primary {
            background-color: color(pokemonYellow);
            color: black;
        }

        .cz2__btn--secondary {
            background: color(pokemonGray);
            color: black;
        }

        .cz2__btn--black:focus {
            &:before {
                border-color: black;
            }
        }
    }
</style>

<script>
    import buildClasses from '../../utilities/buildClasses';

    export default {
        props: {
            primary: {
                type: Boolean,
                default: false,
            },

            secondary: {
                type: Boolean,
                default: false,
            },

            black: {
                type: Boolean,
                default: false,
            },

            blue: {
                type: Boolean,
                default: false,
            },

            flat: {
                type: Boolean,
                default: false,
            },

            narrow: {
                type: Boolean,
                default: false,
            },

            onDark: {
                type: Boolean,
                default: false,
            },

            auto: {
                type: Boolean,
                default: false,
            },

            disabled: {
                type: Boolean,
                default: false,
            },

            error: {
                type: Boolean,
                default: false,
            },

            as: {
                type: String,
                default: null,
            },
        },

        computed: {
            isDisabled() {
                return this.disabled;
            },

            getClasses() {
                return buildClasses([
                    'cz2__btn',
                    [this.isDisabled, 'cz2__btn--disabled'],
                    [this.primary, 'cz2__btn--primary'],
                    [this.secondary, 'cz2__btn--secondary'],
                    [this.black, 'cz2__btn--black'],
                    [this.blue, 'cz2__btn--blue'],
                    [this.auto, 'cz2__btn--auto'],
                    [this.narrow, 'cz2__btn--narrow'],
                    [this.error, 'cz2__btn--error'],
                    [this.onDark, 'on-dark-bg'],
                    [this.flat, 'cz2__btn--flat'],
                ]);
            },
        },

        methods: {
            click() {
                this.$emit('click');
            },
        },
    };
</script>
